import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  Typography,
  Button,
  Box,
  Dialog,
  DialogContent,
  Grid,
  Chip,
  IconButton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@material-ui/core';

import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Chat as ChatIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AttachMoney as MoneyIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  AccessTime as AccessTimeIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Add as AddIcon,
} from '@material-ui/icons';
import api from '../../services/api';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import Title from '../../components/Title';
import usePipelines from '../../hooks/usePipelines';
import useDeals from '../../hooks/useDeals';
import usePermissions from '../../hooks/usePermissions';
import useUsers from '../../hooks/useUsers';
import './responsive.css';
import IdleStatusBadge from '../../components/IdleStatusBadge';
import TicketResponseTimeIndicator from '../../components/TicketResponseTimeIndicator';
import { useIdleStatusUpdates } from '../../hooks/useIdleStatusUpdates';
import NewTicketModal from '../../components/NewTicketModal';
import DealActivityHistory from '../../components/DealActivityHistory';
import StageActivitiesModal from '../../components/StageActivitiesModal';
import PreRequirementsModal from '../../components/PreRequirementsModal';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1),
    minHeight: '100vh',
    maxWidth: '100%',
    width: '100%',
    boxSizing: 'border-box',
  },
  header: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    fontWeight: 'bold',
  },
  kanbanWrapper: {
    width: '100%',
    height: 'calc(100vh - 160px)', // Reduzido para dar mais espaço
    minHeight: '400px', // Altura mínima para garantir usabilidade
    overflowX: 'auto',
    overflowY: 'hidden',
    // Estilos customizados para scrollbar horizontal
    '&::-webkit-scrollbar': {
      height: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(0, 0, 0, 0.1)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.primary.main,
      borderRadius: '4px',
      '&:hover': {
        background: theme.palette.primary.dark,
      },
    },
    // Para Firefox
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.primary.main} rgba(0, 0, 0, 0.1)`,
  },
  kanbanContainer: {
    display: 'flex',
    minWidth: 'fit-content',
    height: '100%',
    padding: 0,
    flexDirection: 'row',
    gap: theme.spacing(2),
  },
  stageColumn: {
    minWidth: '300px',
    maxWidth: '350px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%', // Usa 100% da altura do kanbanWrapper
    maxHeight: 'calc(100vh - 180px)', // Altura máxima baseada na viewport
  },
  stageHeader: {
    padding: theme.spacing(2),
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative', // Para posicionar os indicadores
  },
  stageBody: {
    flex: 1,
    minHeight: '200px', // Altura mínima reduzida para melhor adaptação
    padding: theme.spacing(1),
    backgroundColor: theme.palette.action.hover,
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    overflowY: 'auto',
    // Estilos customizados para scrollbar
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(0, 0, 0, 0.1)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.primary.main,
      borderRadius: '4px',
      '&:hover': {
        background: theme.palette.primary.dark,
      },
    },
    // Para Firefox
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.primary.main} rgba(0, 0, 0, 0.1)`,
  },
  dealCard: {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: '8px',
    border: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
      transform: 'translateY(-2px)',
    },
  },
  dealCardDragging: {
    backgroundColor: theme.palette.action.hover,
    boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
    transform: 'rotate(5deg)',
  },
  modal: {
    '& .MuiDialog-paper': {
      minWidth: '600px',
      maxWidth: '800px',
      width: '90%',
    },
  },
  // Header do modal (versão "moderna" sem sx — compatível com MUI v4)
  modalHeaderModern: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(4),
    borderRadius: '16px 16px 0 0',
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  modalHeaderDecorTopRight: {
    position: 'absolute',
    top: -50,
    right: -50,
    width: 100,
    height: 100,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.1)',
  },
  modalHeaderDecorBottomLeft: {
    position: 'absolute',
    bottom: -30,
    left: -30,
    width: 60,
    height: 60,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.1)',
  },
  modalHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    zIndex: 1,
    gap: theme.spacing(2),
  },
  modalTitleWrap: {
    minWidth: 0, // necessário para ellipsis dentro de flex
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  modalTitleRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  modalTitleName: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 700,
  },
  modalTitleTicketId: {
    minWidth: 0,
    maxWidth: '100%',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    opacity: 0.9,
    fontSize: '0.875rem',
  },
  modalCloseButton: {
    flexShrink: 0,
    backgroundColor: 'rgba(255,255,255,0.2)',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.3)',
    },
    // garantir branco mesmo em temas/overrides
    '&, & svg': {
      color: '#fff',
    },
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: theme.spacing(2),
  },
  modalTitle: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  closeButton: {
    color: theme.palette.text.secondary,
  },
  cardInfo: {
    marginBottom: theme.spacing(3),
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  infoIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '1.2rem',
  },
  infoText: {
    color: theme.palette.text.primary,
  },
  valueDisplay: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  valueAmount: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1),
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  chatButton: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 'bold',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  stageIndicator: {
    position: 'absolute',
    top: -8,
    right: -8,
    width: 16,
    height: 16,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  availableStage: {
    backgroundColor: '#4caf50',
  },
  unavailableStage: {
    backgroundColor: '#f44336',
  },
  createTicketButton: {
    width: '100%',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: 'transparent',
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    color: theme.palette.text.secondary,
    textTransform: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
    minHeight: '48px', // Altura fixa para garantir consistência
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
  },
  createTicketButtonPlaceholder: {
    width: '100%',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minHeight: '48px', // Mesma altura do botão real
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const CrmList = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const { id: crmId } = useParams();
  const { findById, findAll: findAllPipelines } = usePipelines();
  const { findOneById } = useDeals();
  const { permissions } = usePermissions();
  const { users } = useUsers();

  const [crmInfo, setCrmInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedDealIdForHistory, setSelectedDealIdForHistory] = useState(null);
  const [contactDealsFromAllPipelines, setContactDealsFromAllPipelines] = useState([]);
  const [stagesForActivityHistory, setStagesForActivityHistory] = useState([]);
  const [loadingContactDealsAllPipelines, setLoadingContactDealsAllPipelines] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [dealActivitiesModalOpen, setDealActivitiesModalOpen] = useState(false);

  const [ticketAttendanceCounts, setTicketAttendanceCounts] = useState(null);
  const [loadingTicketCounts, setLoadingTicketCounts] = useState(false);
  const [selectedTicketIdleStatus, setSelectedTicketIdleStatus] = useState(null);
  const { idleStatusUpdates } = useIdleStatusUpdates();

  const [stageRequirements, setStageRequirements] = useState([]);
  const isProcessingRef = useRef(false);

  // Estados para modal de pré-requisitos pendentes
  const [preRequirementsModalOpen, setPreRequirementsModalOpen] =
    useState(false);
  const [dealIdForPreRequirements, setDealIdForPreRequirements] =
    useState(null);
  const [targetStageIdForPreRequirements, setTargetStageIdForPreRequirements] =
    useState(null);

  const [kanbanData, setKanbanData] = useState({
    stages: [],
    deals: [],
  });

  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Estados para validação de movimento
  const [availableStages, setAvailableStages] = useState({});
  const [movementValidation, setMovementValidation] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [dragSource, setDragSource] = useState(null);

  const selectedDealTicket = useMemo(() => {
    if (!selectedCard) {
      return null;
    }

    const ensureDealId = ticketData =>
      ticketData
        ? {
          ...ticketData,
          dealId: ticketData.dealId || selectedCard.id,
        }
        : null;

    if (selectedCard.ticket) {
      return ensureDealId(selectedCard.ticket);
    }

    if (selectedCard.tickets && selectedCard.tickets.length > 0) {
      const openTicket = selectedCard.tickets.find(
        ticketItem => ticketItem.status === 'open'
      );
      return ensureDealId(openTicket || selectedCard.tickets[0]);
    }

    return null;
  }, [selectedCard]);

  const formatDurationMs = ms => {
    const safeMs = Number(ms);
    if (!Number.isFinite(safeMs) || safeMs <= 0) return null;

    const totalMinutes = Math.floor(safeMs / (1000 * 60));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    if (days > 0) {
      return `${days}d ${hours}h`;
    }
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const isSelectedDealClosed = useMemo(() => {
    if (!selectedCard) return false;

    if (selectedCard.status === 'closed') return true;
    if (selectedCard.ticket?.status === 'closed') return true;
    if (selectedDealTicket?.status === 'closed') return true;

    if (
      Array.isArray(selectedCard.tickets) &&
      selectedCard.tickets.some(t => t?.status === 'closed')
    ) {
      return true;
    }

    return false;
  }, [selectedCard, selectedDealTicket]);

  const averageAttendanceTimeDisplay = useMemo(() => {
    if (!isSelectedDealClosed || !selectedCard) return null;

    const allTickets = [];
    if (selectedCard.ticket) allTickets.push(selectedCard.ticket);
    if (Array.isArray(selectedCard.tickets)) allTickets.push(...selectedCard.tickets);

    // Deduplicar por id (evita contar o mesmo ticket duas vezes)
    const uniqueTickets = new Map();
    allTickets.forEach(t => {
      if (t?.id != null && !uniqueTickets.has(t.id)) {
        uniqueTickets.set(t.id, t);
      }
    });

    const closedTickets = Array.from(uniqueTickets.values()).filter(
      t => t?.status === 'closed'
    );

    const durationsMs = closedTickets
      .map(t => {
        const start = t?.startedAt || t?.createdAt;
        const end = t?.closedAt || t?.updatedAt || t?.finishedAt;
        if (!start || !end) return null;

        const ms = new Date(end).getTime() - new Date(start).getTime();
        return Number.isFinite(ms) && ms > 0 ? ms : null;
      })
      .filter(ms => ms != null);

    if (durationsMs.length > 0) {
      const avgMs =
        durationsMs.reduce((acc, ms) => acc + ms, 0) / durationsMs.length;
      return formatDurationMs(avgMs);
    }

    // Fallback: tentar usar datas do próprio deal (se existirem)
    const dealStart = selectedCard?.createdAt;
    const dealEnd = selectedCard?.closedAt || selectedCard?.updatedAt;
    if (dealStart && dealEnd) {
      const ms = new Date(dealEnd).getTime() - new Date(dealStart).getTime();
      return formatDurationMs(ms);
    }

    return null;
  }, [isSelectedDealClosed, selectedCard]);

  const selectedCardId = selectedCard?.id;

  // Lista de deals do contato: inicia só da pipeline atual; outras pipelines só após usuário solicitar
  // Cada deal recebe stageName (nome do estágio onde está) para exibir corretamente no select
  const contactDealsList = useMemo(() => {
    if (!selectedCard?.contact?.id) return [];
    const contactId = selectedCard.contact.id;
    if (contactDealsFromAllPipelines.length > 0) {
      return contactDealsFromAllPipelines;
    }
    const stages = selectedPipeline?.stages || kanbanData.stages || [];
    if (!stages.length) return [];
    const allDeals = stages.flatMap(s =>
      (s.deals || []).map(d => ({ ...d, stageName: s.name }))
    );
    const byContact = allDeals.filter(d => d.contact?.id === contactId);
    const seen = new Set();
    const unique = byContact.filter(d => {
      if (seen.has(d.id)) return false;
      seen.add(d.id);
      return true;
    });
    const pipelineId = selectedPipeline?.id ?? crmId;
    const pipelineName = selectedPipeline?.name ?? crmInfo?.name ?? '-';
    return unique
      .sort((a, b) => (a.id || 0) - (b.id || 0))
      .map(d => ({
        ...d,
        pipelineId: d.pipelineId ?? pipelineId,
        pipelineName: d.pipelineName ?? pipelineName,
        stageName: d.stageName ?? d.stage?.name,
      }));
  }, [selectedCard?.contact?.id, selectedPipeline?.stages, selectedPipeline?.id, selectedPipeline?.name, kanbanData.stages, contactDealsFromAllPipelines, crmId, crmInfo?.name]);

  useEffect(() => {
    if (modalOpen && selectedCard?.id) {
      setSelectedDealIdForHistory(selectedCard.id);
    }
  }, [modalOpen, selectedCard?.id]);

  // Buscar todos os deals do contato em todas as pipelines (GET /pipelines/:id?contactId=...)
  // Otimizado: 1 GET /pipelines + N GET /pipelines/:id em paralelo; stages reutilizados no histórico
  const fetchContactDealsFromAllPipelines = useCallback(async contactId => {
    if (!contactId) {
      setContactDealsFromAllPipelines([]);
      setStagesForActivityHistory([]);
      return;
    }
    setLoadingContactDealsAllPipelines(true);
    setContactDealsFromAllPipelines([]);
    setStagesForActivityHistory([]);
    try {
      const pipelines = await findAllPipelines();
      if (!Array.isArray(pipelines) || pipelines.length === 0) {
        setLoadingContactDealsAllPipelines(false);
        return;
      }
      // Montar stages a partir de GET /pipelines para passar ao DealActivityHistory (evita nova chamada)
      const allStages = [];
      pipelines.forEach(p => {
        if (p?.stages && Array.isArray(p.stages)) {
          p.stages.forEach(stage => {
            allStages.push({
              ...stage,
              pipelineId: p.id,
              pipelineName: p.name || '-',
            });
          });
        }
      });
      setStagesForActivityHistory(allStages);

      // Buscar deals por pipeline em paralelo (não sequencial)
      const pipelinesWithId = pipelines.filter(p => p?.id);
      const results = await Promise.all(
        pipelinesWithId.map(p =>
          findById(p.id, { params: { contactId } }).catch(() => null)
        )
      );

      const allDeals = [];
      results.forEach((pipelineData, idx) => {
        if (!pipelineData?.stages) return;
        const pipeline = pipelinesWithId[idx];
        pipelineData.stages.forEach(stage => {
          (stage.deals || []).forEach(d => {
            allDeals.push({
              ...d,
              stageName: stage.name,
              pipelineId: d.pipelineId ?? pipeline?.id,
              pipelineName: pipeline?.name || pipelineData?.name || '-',
            });
          });
        });
      });

      const seen = new Set();
      const unique = allDeals.filter(d => {
        if (seen.has(d.id)) return false;
        seen.add(d.id);
        return true;
      });
      setContactDealsFromAllPipelines(unique.sort((a, b) => (a.id || 0) - (b.id || 0)));
    } catch (err) {
      console.warn('Erro ao buscar deals do contato em todas as pipelines:', err);
      setContactDealsFromAllPipelines([]);
      setStagesForActivityHistory([]);
    } finally {
      setLoadingContactDealsAllPipelines(false);
    }
  }, [findAllPipelines, findById]);

  // Só limpar ao fechar o modal; não buscar outras pipelines ao abrir
  useEffect(() => {
    if (!modalOpen) {
      setContactDealsFromAllPipelines([]);
      setStagesForActivityHistory([]);
    }
  }, [modalOpen]);

  // Stages da pipeline atual para o histórico (sem buscar outras pipelines)
  const stagesFromCurrentPipeline = useMemo(() => {
    if (!selectedPipeline?.stages?.length) return [];
    return selectedPipeline.stages.map(s => ({
      ...s,
      pipelineId: selectedPipeline.id,
      pipelineName: selectedPipeline.name || '-',
    }));
  }, [selectedPipeline]);

  const stagesForDealActivityHistory =
    stagesForActivityHistory.length > 0 ? stagesForActivityHistory : stagesFromCurrentPipeline;

  const getDealStatusLabelForCrm = deal => {
    if (deal.status === 'closed') return 'Concluído';
    if (deal.status === 'lost') return 'Perdido';
    return 'Em andamento';
  };

  const formatDealDateForCrm = deal => {
    const dateStr = deal.createdAt || deal.updatedAt;
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return Number.isFinite(date.getTime())
        ? date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : '';
    } catch {
      return '';
    }
  };

  // Função para buscar informações da pipeline
  const fetchPipelineInfo = useCallback(async () => {
    try {
      setLoading(true);

      const pipelineData = await findById(crmId);

      if (!pipelineData) {
        toast.error('Pipeline não encontrada');
        return;
      }

      if (!isMountedRef.current) return;

      setSelectedPipeline(pipelineData);
      setCrmInfo({
        id: pipelineData.id,
        name: pipelineData.name,
        description: pipelineData.description,
      });

      // Organizar dados do Kanban diretamente da resposta da API
      if (pipelineData.stages && pipelineData.stages.length > 0) {
        const stages = pipelineData.stages
          .sort((a, b) => a.order - b.order)
          .map(stage => {
            // Coletar todos os deals de todos os estágios
            const allDeals = pipelineData.stages.flatMap(s => s.deals || []);

            // Filtrar deals baseado no status e nome do estágio
            let filteredDeals = [];

            if (
              stage.name &&
              (stage.name.toLowerCase().includes('perdido') ||
                stage.name.toLowerCase().includes('lost'))
            ) {
              // Estágio de perdidos - pegar deals com status 'lost'
              filteredDeals = allDeals.filter(
                deal =>
                  deal.status === 'lost' ||
                  (deal.tickets &&
                    deal.tickets.some(ticket => ticket.status === 'lost'))
              );
            } else {
              // Outros estágios - pegar deals que não são lost nem closed
              filteredDeals = (stage.deals || []).filter(
                deal =>
                  deal.status !== 'lost' &&
                  deal.status !== 'closed' &&
                  (!deal.tickets ||
                    !deal.tickets.some(
                      ticket =>
                        ticket.status === 'lost' || ticket.status === 'closed'
                    ))
              );
            }

            return {
              ...stage,
              deals: filteredDeals,
            };
          });

        setKanbanData({ stages, deals: [] });
      }
    } catch (error) {
      toast.error(i18n.t('pipelines.errors.fetchError'));
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [crmId, findById]);

  // Função para organizar dados no formato do Kanban
  const organizeKanbanData = useCallback(() => {
    if (!selectedPipeline || !selectedPipeline.stages) {
      return;
    }

    const stages = selectedPipeline.stages
      .sort((a, b) => a.order - b.order)
      .map(stage => {
        // Coletar todos os deals de todos os estágios
        const allDeals = selectedPipeline.stages.flatMap(s => s.deals || []);

        // Filtrar deals baseado no status e nome do estágio
        let filteredDeals = [];

        if (
          stage.name.toLowerCase().includes('perdido') ||
          stage.name.toLowerCase().includes('lost')
        ) {
          // Estágio de perdidos - pegar deals com status 'lost'
          filteredDeals = allDeals.filter(
            deal =>
              deal.status === 'lost' ||
              (deal.tickets &&
                deal.tickets.some(ticket => ticket.status === 'lost'))
          );
        } else if (
          stage.name.toLowerCase().includes('concluído') ||
          stage.name.toLowerCase().includes('concluido') ||
          stage.name.toLowerCase().includes('closed') ||
          stage.name.toLowerCase().includes('finalizado')
        ) {
          // Estágio de concluído - pegar deals com status 'closed'
          filteredDeals = allDeals.filter(
            deal =>
              deal.status === 'closed' ||
              (deal.tickets &&
                deal.tickets.some(ticket => ticket.status === 'closed'))
          );
        } else {
          // Outros estágios - pegar deals que não são lost nem closed
          filteredDeals = (stage.deals || []).filter(
            deal =>
              deal.status !== 'lost' &&
              deal.status !== 'closed' &&
              (!deal.tickets ||
                !deal.tickets.some(
                  ticket =>
                    ticket.status === 'lost' || ticket.status === 'closed'
                ))
          );
        }

        return {
          ...stage,
          deals: filteredDeals,
        };
      });

    setKanbanData({ stages, deals: [] });
  }, [selectedPipeline]);

  // Função para buscar estágios disponíveis para movimento
  const fetchAvailableStages = async dealId => {
    try {
      const response = await api.get(`/deals/${dealId}/available-stages`);
      return response.data;
    } catch (err) {
      return {
        availableStages: [],
        reason: 'Erro ao carregar estágios disponíveis',
      };
    }
  };

  // Função para buscar o usuário responsável pela deal
  const getDealResponsibleUser = useCallback(
    deal => {
      if (!deal || !deal.tickets || deal.tickets.length === 0) {
        return null;
      }

      const ticket =
        deal.tickets && deal.tickets.length > 0 ? deal.tickets[0] : null;

      if (!ticket || !ticket.userId) {
        return null;
      }

      // Buscar o usuário na lista de usuários carregada
      const responsibleUser = users.find(user => user.id === ticket.userId);
      return responsibleUser;
    },
    [users]
  );

  // Função para verificar pré-requisitos antes de mover para "Perdidos"
  const checkPreRequirementsBeforeMove = useCallback(
    async (dealId, pipelineId) => {
      try {
        // 1. Buscar pré-requisitos configurados para "Perdidos" na pipeline
        const { data: preReqData } = await api.get(
          `/pipelines/${pipelineId}/lost-stage-pre-requirements`
        );
        const configuredPreRequirements = preReqData.preRequirements || [];

        if (configuredPreRequirements.length === 0) {
          // Não há pré-requisitos configurados, pode mover
          return { canMove: true, unmetRequirements: [] };
        }

        // 2. Buscar requirements do deal
        const { data: requirementsData } = await api.get(
          `/deals/${dealId}/requirements`
        );
        const dealRequirements = requirementsData.requirements || [];

        // 3. Criar um mapa dos requirements do deal por ID
        const dealRequirementsMap = new Map(
          dealRequirements.map(req => [req.id, req])
        );

        // 4. Filtrar apenas os pré-requisitos configurados que são obrigatórios e não estão preenchidos
        const unmetRequirements = configuredPreRequirements
          .map(preReq => {
            const requirement =
              preReq.requirement ||
              dealRequirementsMap.get(preReq.requirementId);
            if (!requirement) return null;

            // Verificar se está preenchido
            const isFilled = requirement.filled || false;

            // Retornar apenas se for obrigatório e não estiver preenchido
            if (requirement.required && !isFilled) {
              return requirement;
            }
            return null;
          })
          .filter(req => req !== null);

        return {
          canMove: unmetRequirements.length === 0,
          unmetRequirements,
        };
      } catch (err) {
        console.error('Erro ao verificar pré-requisitos:', err);
        // Em caso de erro, permitir movimento (não bloquear)
        return { canMove: true, unmetRequirements: [] };
      }
    },
    []
  );

  // Função para validar movimento em tempo real
  const validateMovement = async (dealId, targetStageId) => {
    try {
      const response = await api.get(
        `/deals/${dealId}/validate-movement?targetStageId=${targetStageId}`
      );
      const validationData = response.data;

      return {
        isValid: validationData.canMove && validationData.movementAllowed,
        canMove: validationData.canMove,
        movementAllowed: validationData.movementAllowed,
        requirementsMet: validationData.requirementsMet,
        currentStage: validationData.currentStage,
        targetStage: validationData.targetStage,
        requirements: validationData.requirements || {},
        reason: validationData.targetStage?.reason || null,
        preRequirementsModalOpened: false,
      };
    } catch (err) {
      // Verificar se é erro de pré-requisitos na validação
      const errorCode =
        err.response?.data?.code || err.response?.data?.error || '';
      const isPreRequirementError =
        errorCode === 'ERR_CANNOT_MOVE_TO_PERDIDOS_WITHOUT_PRE_REQUIREMENTS' ||
        errorCode.includes('ERR_CANNOT_MOVE_TO_PERDIDOS') ||
        errorCode.includes('PRE_REQUIREMENTS') ||
        err.response?.data?.message?.includes('pré-requirement') ||
        err.response?.data?.message?.includes('pre-requirement');

      if (isPreRequirementError) {
        // Abrir modal diretamente da validação
        setDealIdForPreRequirements(dealId);
        setTargetStageIdForPreRequirements(targetStageId);
        setPreRequirementsModalOpen(true);

        return {
          isValid: false,
          canMove: false,
          movementAllowed: false,
          requirementsMet: false,
          currentStage: null,
          targetStage: { id: targetStageId },
          requirements: {},
          reason: 'Pré-requisitos não cumpridos',
          preRequirementsModalOpened: true,
        };
      }

      return {
        isValid: false,
        canMove: false,
        movementAllowed: false,
        requirementsMet: false,
        currentStage: null,
        targetStage: null,
        requirements: {},
        reason: 'Erro ao validar movimento',
        preRequirementsModalOpened: false,
      };
    }
  };

  // Função para atualizar estágio
  const updateDealStage = useCallback(async (dealId, newStageId) => {
    try {
      await api.patch(`/deals/${dealId}/stages/${newStageId}`);
      return true;
    } catch (err) {
      // Verificar se é erro de pré-requisitos - verificar múltiplas possibilidades
      const errorCode =
        err.response?.data?.code ||
        err.response?.data?.error ||
        err.response?.data?.message ||
        '';
      const isPreRequirementError =
        errorCode === 'ERR_CANNOT_MOVE_TO_PERDIDOS_WITHOUT_PRE_REQUIREMENTS' ||
        errorCode.includes('ERR_CANNOT_MOVE_TO_PERDIDOS') ||
        errorCode.includes('PRE_REQUIREMENTS') ||
        err.response?.data?.message?.includes('pré-requirement') ||
        err.response?.data?.message?.includes('pre-requirement') ||
        err.response?.data?.message?.includes('pre requirement');

      if (isPreRequirementError) {
        // Abrir modal de pré-requisitos
        setDealIdForPreRequirements(dealId);
        setTargetStageIdForPreRequirements(newStageId);
        setPreRequirementsModalOpen(true);
        return false;
      }

      toast.error('Erro ao mover card. Tente novamente.');
      return false;
    }
  }, []);

  // Função para retornar card ao estágio anterior
  const returnCardToPreviousStage = async (dealId, currentStage) => {
    try {
      if (currentStage && currentStage.id) {
        toast.info('Card retornado ao estágio anterior');
      }
    } catch (err) {
      toast.error('Erro ao retornar card ao estágio anterior');
    }
  };

  // Handler do drag & drop
  const handleDragEnd = useCallback(
    async result => {
      const { source, destination, draggableId } = result;

      // Limpar estados de drag
      setIsDragging(false);
      setDragSource(null);
      setAvailableStages({});
      setMovementValidation({});

      if (!destination) {
        return;
      }

      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      ) {
        return;
      }

      // Evitar processamento duplicado
      if (isProcessingRef.current) {
        return;
      }

      isProcessingRef.current = true;

      try {
        const dealId = draggableId;
        const newStageId = destination.droppableId;

        // 1. Verificar se o estágio destino é "Perdidos"
        const targetStage = kanbanData.stages.find(
          s => s.id.toString() === newStageId
        );
        const isPerdidosStage =
          targetStage &&
          (targetStage.name.toLowerCase().includes('perdido') ||
            targetStage.name.toLowerCase().includes('lost'));

        // 2. Se for estágio "Perdidos", verificar pré-requisitos ANTES de tentar mover
        if (isPerdidosStage && crmId) {
          const preReqCheck = await checkPreRequirementsBeforeMove(
            dealId,
            crmId
          );

          if (!preReqCheck.canMove) {
            // Há pré-requisitos obrigatórios não preenchidos
            // Abrir modal e NÃO fazer a requisição de movimento
            setDealIdForPreRequirements(dealId);
            setTargetStageIdForPreRequirements(newStageId);
            setPreRequirementsModalOpen(true);

            // Remover o card do estágio destino incorreto
            setKanbanData(prev => {
              const updatedStages = prev.stages.map(stage => {
                if (stage.id.toString() === destination.droppableId) {
                  return {
                    ...stage,
                    deals: stage.deals.filter(
                      deal => deal.id.toString() !== dealId
                    ),
                  };
                }
                return stage;
              });
              return { ...prev, stages: updatedStages };
            });
            return;
          }
          // Se todos os pré-requisitos estão preenchidos, continuar com o movimento
        }

        // 3. Validar movimento em tempo real (para outros estágios)
        const validation = await validateMovement(dealId, newStageId);

        // Se a validação falhar, bloquear
        if (!validation.movementAllowed) {
          // Mostrar toast com motivo da validação
          const reason = validation.reason || 'Movimento não permitido';
          toast.error(`Não é possível mover para este estágio: ${reason}`, {
            autoClose: 5000,
          });

          // Remover o card do estágio destino incorreto
          setKanbanData(prev => {
            const updatedStages = prev.stages.map(stage => {
              if (stage.id.toString() === destination.droppableId) {
                return {
                  ...stage,
                  deals: stage.deals.filter(
                    deal => deal.id.toString() !== dealId
                  ),
                };
              }
              return stage;
            });
            return { ...prev, stages: updatedStages };
          });

          // Retornar o card para o estágio anterior
          await returnCardToPreviousStage(dealId, validation.currentStage);
          return;
        }

        // 4. Atualizar no backend (movimento já foi validado, então deve ser permitido)
        const success = await updateDealStage(dealId, newStageId);

        if (!success) {
          // Se falhar ao atualizar, o modal de pré-requisitos já foi mostrado se necessário
          // Remover o card do estágio destino incorreto
          setKanbanData(prev => {
            const updatedStages = prev.stages.map(stage => {
              if (stage.id.toString() === destination.droppableId) {
                return {
                  ...stage,
                  deals: stage.deals.filter(
                    deal => deal.id.toString() !== dealId
                  ),
                };
              }
              return stage;
            });
            return { ...prev, stages: updatedStages };
          });
          await returnCardToPreviousStage(dealId, validation.currentStage);
          return;
        }

        // 3. Atualizar estado local
        const updatedStages = kanbanData.stages.map(stage => {
          if (stage.id.toString() === source.droppableId) {
            // Remover deal do estágio origem
            return {
              ...stage,
              deals: stage.deals.filter(deal => deal.id.toString() !== dealId),
            };
          }

          if (stage.id.toString() === destination.droppableId) {
            // Adicionar deal ao estágio destino
            const movedDeal = kanbanData.stages
              .find(s => s.id.toString() === source.droppableId)
              ?.deals.find(d => d.id.toString() === dealId);

            if (movedDeal) {
              return {
                ...stage,
                deals: [
                  ...stage.deals,
                  { ...movedDeal, stageId: parseInt(newStageId) },
                ],
              };
            }
          }

          return stage;
        });

        setKanbanData(prev => ({ ...prev, stages: updatedStages }));

        // Mostrar mensagem de sucesso com informações do estágio destino
        const targetStageName = validation.targetStage?.name || 'novo estágio';
        toast.success(`Card movido com sucesso para ${targetStageName}!`);
      } catch (error) {
        toast.error('Erro ao mover card');
      } finally {
        isProcessingRef.current = false;
      }
    },
    [kanbanData.stages, updateDealStage, checkPreRequirementsBeforeMove, crmId]
  );

  // Handler para início do drag
  const handleDragStart = useCallback(result => {
    const { source, draggableId } = result;
    setIsDragging(true);
    setDragSource(source.droppableId);

    // Buscar estágios disponíveis para o deal sendo arrastado
    fetchAvailableStages(draggableId)
      .then(result => {
        const stages = result.availableStages || [];
        if (Array.isArray(stages)) {
          setAvailableStages(prev => ({
            ...prev,
            [draggableId]: stages,
          }));
        } else {
          setAvailableStages(prev => ({
            ...prev,
            [draggableId]: [],
          }));
        }
      })
      .catch(() => {
        setAvailableStages(prev => ({
          ...prev,
          [draggableId]: [],
        }));
      });
  }, []);

  // Handler para durante o drag
  const handleDragUpdate = useCallback(
    result => {
      const { destination, draggableId } = result;

      if (destination && availableStages[draggableId]) {
        const stages = availableStages[draggableId];
        if (!Array.isArray(stages)) return;

        const targetStageId = destination.droppableId;
        const isAvailable = stages.some(
          stage => stage.id.toString() === targetStageId
        );

        setMovementValidation(prev => ({
          ...prev,
          [draggableId]: {
            targetStageId,
            isValid: isAvailable,
          },
        }));
      }
    },
    [availableStages]
  );

  // Handler para cancelamento de drag
  const handleDragCancel = useCallback(() => {
    setIsDragging(false);
    setDragSource(null);
    setAvailableStages({});
    setMovementValidation({});
  }, []);

  // Funções helper para verificar estado durante o render
  const isStageAvailableForCurrentDrag = useCallback(
    stageId => {
      if (!isDragging || !dragSource) return false;

      const currentDragId = Object.keys(availableStages)[0];
      if (!currentDragId || !availableStages[currentDragId]) return false;

      const stages = availableStages[currentDragId];
      if (!Array.isArray(stages)) return false;

      return stages.some(stage => stage.id === stageId);
    },
    [isDragging, dragSource, availableStages]
  );

  const isCurrentDragValid = useCallback(() => {
    if (!isDragging) return false;

    const currentDragId = Object.keys(movementValidation)[0];
    if (!currentDragId || !movementValidation[currentDragId]) return false;

    return movementValidation[currentDragId].isValid;
  }, [isDragging, movementValidation]);

  // Função para lidar com clique no card
  const handleCardClick = useCallback(
    async dealId => {
      try {
        const foundDeal = await findOneById(dealId);

        if (foundDeal) {
          if (!isMountedRef.current) {
            return;
          }
          setSelectedCard(foundDeal);
          setModalOpen(true);
        } else {
          toast.error('Deal não encontrado');
        }
      } catch (error) {
        toast.error('Erro ao carregar detalhes do deal');
      }
    },
    [findOneById]
  );

  // Abre automaticamente o card indicado por ?dealId= na URL.
  // É o que o atalho "Abrir lead na pipeline" da tela de atendimento usa para
  // cair direto no card do lead em vez de só na pipeline.
  const autoOpenedDealRef = useRef(null);
  useEffect(() => {
    const dealIdParam = new URLSearchParams(location.search).get('dealId');
    if (!dealIdParam) return;
    // Evita reabrir o mesmo card se o usuário fechar o modal
    if (autoOpenedDealRef.current === dealIdParam) return;

    autoOpenedDealRef.current = dealIdParam;
    handleCardClick(Number(dealIdParam));

    // Limpa o parâmetro para que recarregar ou voltar não reabra o card
    history.replace(location.pathname);
  }, [location.search, location.pathname, handleCardClick, history]);

  // Função para fechar modal
  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedCard(null);
    setDealActivitiesModalOpen(false);
  }, []);

  // Função para voltar ao CRM
  const handleBackToCrm = () => {
    history.push('/crm');
  };

  // Função para ir para o chat
  const handleGoToChat = cardId => {
    const deal = kanbanData.stages
      .flatMap(stage => stage.deals)
      .find(d => d.id.toString() === cardId.toString());

    if (deal && deal.ticket) {
      const ticketUuid = deal.ticket.uuid;
      history.push(`/tickets/${ticketUuid}`);
      handleCloseModal();
    } else {
      if (
        selectedCard &&
        selectedCard.tickets &&
        selectedCard.tickets.length > 0
      ) {
        const ticketUuid = selectedCard.ticket.uuid;
        history.push(`/tickets/${ticketUuid}`);
        handleCloseModal();
      } else {
        toast.error('Nenhum ticket encontrado para este deal');
      }
    }
  };

  // Função para abrir modal de criação de ticket
  const handleCreateTicket = stageId => {
    setNewTicketModalOpen(true);
  };

  // Função para fechar modal de criação de ticket
  const handleCloseNewTicketModal = ticket => {
    setNewTicketModalOpen(false);

    // Se um ticket foi criado, recarregar os dados da pipeline
    if (ticket) {
      fetchPipelineInfo();
      toast.success('Ticket criado com sucesso!');
    }
  };

  const handleOpenDealActivitiesModal = useCallback(() => {
    if (!selectedDealTicket) {
      toast.error('Nenhum ticket vinculado a este negócio.');
      return;
    }
    setDealActivitiesModalOpen(true);
  }, [selectedDealTicket]);

  const handleCloseDealActivitiesModal = useCallback(() => {
    setDealActivitiesModalOpen(false);
  }, []);

  const handleDealActivitiesSaved = useCallback(async () => {
    try {
      if (selectedCardId) {
        const updatedDeal = await findOneById(selectedCardId);
        if (isMountedRef.current && updatedDeal) {
          setSelectedCard(updatedDeal);
        }
      }
    } catch (error) {
      toast.error(
        'Não foi possível atualizar o negócio após salvar as atividades.'
      );
    } finally {
      fetchPipelineInfo();
    }
  }, [selectedCardId, findOneById, fetchPipelineInfo]);

  // Função para formatar telefone
  const formatPhoneNumber = number => {
    if (!number) return '';
    const cleaned = number.replace(/\D/g, '');
    const withoutCountry = cleaned.startsWith('55')
      ? cleaned.slice(2)
      : cleaned;

    if (withoutCountry.length === 11) {
      return `+55 (${withoutCountry.slice(0, 2)}) ${withoutCountry.slice(2, 7)}-${withoutCountry.slice(7)}`;
    } else if (withoutCountry.length === 10) {
      return `+55 (${withoutCountry.slice(0, 2)}) ${withoutCountry.slice(2, 6)}-${withoutCountry.slice(6)}`;
    }

    return number;
  };

  // useEffects
  useEffect(() => {
    if (crmId && !selectedPipeline) {
      fetchPipelineInfo();
    }
  }, [crmId, selectedPipeline, fetchPipelineInfo]);

  // Organizar dados quando a pipeline for carregada
  useEffect(() => {
    if (
      selectedPipeline &&
      selectedPipeline.stages &&
      selectedPipeline.stages.length > 0 &&
      kanbanData.stages.length === 0
    ) {
      organizeKanbanData();
    }
  }, [selectedPipeline, kanbanData.stages.length, organizeKanbanData]);

  useEffect(() => {
    if (selectedCard?.id) {
      const fetchRequirements = async () => {
        try {
          const { data } = await api.get(
            `/deals/${selectedCard.id}/requirements`
          );
          if (isMountedRef.current) {
            setStageRequirements(data.requirements);
          }
        } catch (err) {
          toast.error('Não foi possível carregar atividades do negócio');
        }
      };
      fetchRequirements();
    }
  }, [selectedCard]);

  // Buscar concluídos/perdidos do ticket no banco quando abrir o drawer do deal
  useEffect(() => {
    const ticketId = selectedDealTicket?.id;
    if (!ticketId || !selectedCard?.contact?.id) {
      setTicketAttendanceCounts(null);
      return;
    }
    let cancelled = false;
    setLoadingTicketCounts(true);
    setTicketAttendanceCounts(null);
    setSelectedTicketIdleStatus(null);
    api
      .get(`/tickets/${ticketId}`)
      .then(({ data }) => {
        if (!cancelled && isMountedRef.current) {
          setTicketAttendanceCounts({
            concludedCount: data.concludedCount ?? 0,
            lostCount: data.lostCount ?? 0,
          });
          setSelectedTicketIdleStatus(data.idleStatus ?? null);
        }
      })
      .catch(() => {
        if (!cancelled && isMountedRef.current) {
          setTicketAttendanceCounts({ concludedCount: 0, lostCount: 0 });
          setSelectedTicketIdleStatus(null);
        }
      })
      .finally(() => {
        if (!cancelled && isMountedRef.current) {
          setLoadingTicketCounts(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDealTicket?.id, selectedCard?.contact?.id]);

  if (loading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='400px'
      >
        <Typography>Carregando pipeline...</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <Button
            variant='outlined'
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToCrm}
            className={classes.backButton}
          >
            Voltar
          </Button>
          <div>
            <Title className={classes.title}>
              {crmInfo?.name || `Pipeline ${crmId}`}
            </Title>
          </div>
        </div>
      </div>

      {/* Kanban Board */}
      {!kanbanData.stages || kanbanData.stages.length === 0 ? (
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          minHeight='400px'
          flexDirection='column'
        >
          <Typography variant='h6' color='textSecondary' gutterBottom>
            {i18n.t('pipelines.details.noStagesFound')}
          </Typography>
          <Typography variant='body2' color='textSecondary'>
            {i18n.t('pipelines.details.noStagesDescription')}
          </Typography>
        </Box>
      ) : (
        <DragDropContext
          onDragStart={handleDragStart}
          onDragUpdate={handleDragUpdate}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
          style={{
            backgroundColor: theme.palette.action.hover,
          }}
        >
          <div className={`${classes.kanbanWrapper} crm-list-kanban-container`}>
            <div className={classes.kanbanContainer}>
              {kanbanData.stages.map((stage, index) => (
                <div key={stage.id} className={classes.stageColumn}>
                  <div
                    className={classes.stageHeader}
                    style={{
                      borderBottom: `4px solid ${stage.color || '#e3f2fd'}`,
                      backgroundColor:
                        theme.palette.type === 'light'
                          ? '#f0f0f0'
                          : theme.palette.background.paper,
                      color:
                        theme.palette.type === 'light' ? '#333333' : '#ffffff',
                    }}
                  >
                    {/* Indicador de estágio disponível para movimento */}
                    {isDragging &&
                      dragSource &&
                      dragSource !== stage.id.toString() && (
                        <Box
                          className={`${classes.stageIndicator} ${isStageAvailableForCurrentDrag(stage.id)
                            ? classes.availableStage
                            : classes.unavailableStage
                            }`}
                        >
                          {isStageAvailableForCurrentDrag(stage.id) ? (
                            <CheckCircleIcon
                              style={{ fontSize: 12, color: 'white' }}
                            />
                          ) : (
                            <CancelIcon
                              style={{ fontSize: 12, color: 'white' }}
                            />
                          )}
                        </Box>
                      )}
                    <Typography variant='h6' style={{ fontWeight: 'bold' }}>
                      {stage.name}
                    </Typography>
                    <Chip
                      label={stage.deals.length}
                      size='small'
                      style={{ backgroundColor: '#fff', color: '#333' }}
                    />
                  </div>

                  <Droppable droppableId={stage.id.toString()}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`${classes.stageBody} crm-list-stage-body`}
                        style={{
                          backgroundColor: snapshot.isDraggingOver
                            ? isCurrentDragValid()
                              ? '#e8f5e8'
                              : '#ffebee'
                            : undefined,
                          transition: 'background-color 0.2s ease',
                          border: snapshot.isDraggingOver
                            ? isCurrentDragValid()
                              ? '2px solid #4caf50'
                              : '2px solid #f44336'
                            : undefined,
                        }}
                      >
                        {stage.deals.map((deal, dealIndex) => {
                          return (
                            <Draggable
                              key={deal.id.toString()}
                              draggableId={deal.id.toString()}
                              index={dealIndex}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`${classes.dealCard} ${snapshot.isDragging ? classes.dealCardDragging : ''}`}
                                  onClick={() => handleCardClick(deal.id)}
                                >
                                  <Box
                                    display='flex'
                                    alignItems='center'
                                    style={{ marginBottom: '8px', gap: '8px' }}
                                  >
                                    <Typography
                                      variant='subtitle1'
                                      style={{ marginBottom: 0 }}
                                    >
                                      {deal.contact?.name || `Deal ${deal.id}`}
                                    </Typography>
                                    {/* Indicador de ociosidade ao lado do nome */}
                                    {deal?.tickets &&
                                      deal.tickets.length > 0 &&
                                      deal.tickets[0]?.idleStatus &&
                                      deal.tickets[0].fromMe === false && (
                                        <IdleStatusBadge
                                          idleStatus={
                                            deal.tickets[0].idleStatus
                                          }
                                          variant='circle'
                                          size='small'
                                          showTooltip={true}
                                        />
                                      )}
                                  </Box>

                                  <Typography
                                    variant='body2'
                                    color='textSecondary'
                                    gutterBottom
                                  >
                                    <MoneyIcon
                                      style={{
                                        fontSize: 16,
                                        marginRight: 4,
                                        verticalAlign: 'middle',
                                      }}
                                    />
                                    R${' '}
                                    {deal.totalValue?.toLocaleString('pt-BR') ||
                                      '0,00'}
                                  </Typography>

                                  <Typography
                                    variant='caption'
                                    color='textSecondary'
                                  >
                                    {deal.contact?.number || 'Sem telefone'}
                                  </Typography>

                                  {/* Informação do usuário responsável (apenas para admin) */}
                                  {permissions?.isAdmin &&
                                    getDealResponsibleUser(deal) && (
                                      <Box
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          marginTop: 4,
                                          fontSize: '10px',
                                          opacity: 0.8,
                                        }}
                                      >
                                        <PersonIcon
                                          style={{
                                            fontSize: 12,
                                            marginRight: 4,
                                          }}
                                        />
                                        <Typography
                                          variant='caption'
                                          color='textSecondary'
                                          style={{ fontSize: '10px' }}
                                        >
                                          {getDealResponsibleUser(deal)?.name ||
                                            'Usuário não encontrado'}
                                        </Typography>
                                      </Box>
                                    )}

                                  {/* Indicador de status */}
                                  <Box
                                    display='flex'
                                    justifyContent='space-between'
                                    alignItems='center'
                                    mt={1}
                                  >
                                    <Box
                                      display='flex'
                                      gap={0.5}
                                      alignItems='center'
                                    >
                                      {deal.requirements &&
                                        deal.requirements.length > 0 && (
                                          <Chip
                                            size='small'
                                            label={
                                              deal.requirements.every(
                                                r => r.filled
                                              )
                                                ? 'Atividades Concluídas'
                                                : 'Atividades Pendentes'
                                            }
                                            color={
                                              deal.requirements.every(
                                                r => r.filled
                                              )
                                                ? 'primary'
                                                : 'secondary'
                                            }
                                            style={{ fontSize: '10px' }}
                                          />
                                        )}
                                      {deal.requirements &&
                                        deal.requirements.length > 0 && (
                                          <Chip
                                            size='small'
                                            label={`${deal.requirements.filter(r => r.filled).length}/${deal.requirements.length} req`}
                                            color='default'
                                            style={{ fontSize: '10px' }}
                                          />
                                        )}
                                    </Box>
                                  </Box>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>

                  {/* Card de Resumo Financeiro do Estágio */}
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      textAlign: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant='h6'
                        color='textSecondary'
                        sx={{
                          fontWeight: 600,
                        }}
                      >
                        {stage.totalValueFormatted || 'R$ 0,00'}
                      </Typography>
                    </Box>
                    <Typography
                      variant='caption'
                      color='textSecondary'
                      sx={{
                        fontWeight: 500,
                      }}
                    >
                      Total em {stage.name}
                    </Typography>
                  </Box>

                  {/* Botão para Criar Ticket - apenas no primeiro estágio */}
                  {index === 0 ? (
                    <Button
                      className={classes.createTicketButton}
                      startIcon={<AddIcon />}
                      onClick={() => handleCreateTicket(stage.id)}
                      variant='outlined'
                      size='small'
                    >
                      Criar Card
                    </Button>
                  ) : (
                    <div className={classes.createTicketButtonPlaceholder} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </DragDropContext>
      )}

      {/* Modal de Detalhes do Card */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        className={classes.modal}
        maxWidth='md'
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: theme.palette.background.paper,
            borderRadius: 16,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          },
        }}
      >
        {selectedCard && (
          <>
            {/* Header Moderno */}
            <Box className={classes.modalHeaderModern}>
              {/* Padrão de fundo decorativo */}
              <Box className={classes.modalHeaderDecorTopRight} />
              <Box className={classes.modalHeaderDecorBottomLeft} />

              <Box className={classes.modalHeaderRow}>
                <Box className={classes.modalTitleWrap}>
                  <Box className={classes.modalTitleRow}>
                    <Typography
                      variant='h4'
                      className={classes.modalTitleName}
                      noWrap
                    >
                      {selectedCard.contact?.name ||
                        i18n.t('pipelines.details.noName')}
                    </Typography>
                    <Typography
                      variant='body1'
                      className={classes.modalTitleTicketId}
                    >
                      ID: {selectedDealTicket?.id ?? selectedCard.id}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={handleCloseModal}
                  className={classes.modalCloseButton}
                  aria-label={i18n.t('common.close') || 'Fechar'}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            <DialogContent sx={{ p: 0 }}>
              <Box sx={{ p: 3 }}>
                {/* Card Principal - Valor e Status */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    background: `linear-gradient(135deg, ${theme.palette.success.light} 0%, ${theme.palette.success.main} 100%)`,
                    color: theme.palette.success.contrastText,
                    borderRadius: 3,
                    textAlign: 'center',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant='h3' sx={{ fontWeight: 700 }}>
                      R${' '}
                      {(() => {
                        const value = selectedCard.totalValue || 0;
                        // Se for string, tentar converter para número
                        if (typeof value === 'string') {
                          const cleanValue = value
                            .replace(/[^\d,.-]/g, '')
                            .replace(',', '.');
                          const numValue = parseFloat(cleanValue);
                          if (!isNaN(numValue)) {
                            return numValue.toLocaleString('pt-BR');
                          }
                          return value; // Retornar string original se não conseguir converter
                        }
                        return value.toLocaleString('pt-BR');
                      })()}
                    </Typography>
                  </Box>
                  <Typography variant='body1' sx={{ opacity: 0.9 }}>
                    {i18n.t('pipelines.details.totalValue')}
                  </Typography>
                </Paper>

                <Grid container spacing={3}>
                  {/* Informações de Contato */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        height: '100%',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 3,
                        background: theme.palette.background.default,
                      }}
                    >
                      <Typography
                        variant='h6'
                        sx={{
                          mb: 3,
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                        }}
                      >
                        {i18n.t('pipelines.details.contactInfo')}
                      </Typography>

                      <Box sx={{ space: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: theme.palette.action.hover,
                          }}
                        >
                          <PersonIcon
                            sx={{
                              color: theme.palette.primary.main,
                              mr: 2,
                              fontSize: 20,
                            }}
                          />
                          <Box sx={{ pl: 1 }}>
                            <Typography
                              variant='body2'
                              color='textSecondary'
                              sx={{
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                              }}
                            >
                              {i18n.t('pipelines.details.contactName')}
                            </Typography>
                            <Typography
                              variant='body1'
                              sx={{ fontWeight: 500 }}
                            >
                              {selectedCard.contact?.name ||
                                i18n.t('pipelines.details.notAvailable')}
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: theme.palette.action.hover,
                          }}
                        >
                          <PhoneIcon
                            sx={{
                              color: theme.palette.primary.main,
                              mr: 2,
                              fontSize: 20,
                            }}
                          />
                          <Box sx={{ pl: 1 }}>
                            <Typography
                              variant='body2'
                              color='textSecondary'
                              sx={{
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                              }}
                            >
                              {i18n.t('pipelines.details.phone')}
                            </Typography>
                            <Typography
                              variant='body1'
                              sx={{ fontWeight: 500 }}
                            >
                              {formatPhoneNumber(
                                selectedCard.contact?.number
                              ) || i18n.t('pipelines.details.notAvailable')}
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2,
                            p: 2,
                            borderRadius: 2,
                            backgroundColor: theme.palette.action.hover,
                          }}
                        >
                          <EmailIcon
                            sx={{
                              color: theme.palette.primary.main,
                              mr: 2,
                              fontSize: 20,
                            }}
                          />
                          <Box sx={{ pl: 1 }}>
                            <Typography
                              variant='body2'
                              color='textSecondary'
                              sx={{
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                              }}
                            >
                              {i18n.t('pipelines.details.email')}
                            </Typography>
                            <Typography
                              variant='body1'
                              sx={{ fontWeight: 500 }}
                            >
                              {selectedCard.contact?.email ||
                                i18n.t('pipelines.details.notAvailable')}
                            </Typography>
                          </Box>

                        </Box>

                        {selectedCard?.contact?.id && (
                          <>
                            {(() => {
                              const ticketForCounts =
                                selectedDealTicket ||
                                selectedCard?.ticket ||
                                (Array.isArray(selectedCard?.tickets)
                                  ? selectedCard.tickets[0]
                                  : null);

                              const concludedCount =
                                ticketAttendanceCounts?.concludedCount ??
                                ticketForCounts?.concludedCount;
                              const lostCount =
                                ticketAttendanceCounts?.lostCount ??
                                ticketForCounts?.lostCount;

                              const concludedValue =
                                loadingTicketCounts
                                  ? '...'
                                  : concludedCount == null
                                    ? 0
                                    : concludedCount;
                              const lostValue =
                                loadingTicketCounts
                                  ? '...'
                                  : lostCount == null
                                    ? 0
                                    : lostCount;

                              return (
                                <>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      mb: 2,
                                      p: 2,
                                      borderRadius: 2,
                                      backgroundColor: theme.palette.action.hover,
                                    }}
                                  >
                                    <CheckCircleIcon
                                      sx={{
                                        color: theme.palette.success.main,
                                        mr: 2,
                                        fontSize: 20,
                                      }}
                                    />
                                    <Box sx={{ pl: 1 }}>
                                      <Typography
                                        variant='body2'
                                        color='textSecondary'
                                        sx={{
                                          fontSize: '0.75rem',
                                          textTransform: 'uppercase',
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        {i18n.t('tickets.attendanceCountClosedLabel')}
                                      </Typography>
                                      <Typography variant='body1' sx={{ fontWeight: 600 }}>
                                        {concludedValue}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      mb: 2,
                                      p: 2,
                                      borderRadius: 2,
                                      backgroundColor: theme.palette.action.hover,
                                    }}
                                  >
                                    <CancelIcon
                                      sx={{
                                        color: theme.palette.error.main,
                                        mr: 2,
                                        fontSize: 20,
                                      }}
                                    />
                                    <Box sx={{ pl: 1 }}>
                                      <Typography
                                        variant='body2'
                                        color='textSecondary'
                                        sx={{
                                          fontSize: '0.75rem',
                                          textTransform: 'uppercase',
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        {i18n.t('tickets.attendanceCountLostLabel')}
                                      </Typography>
                                      <Typography variant='body1' sx={{ fontWeight: 600 }}>
                                        {lostValue}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </>
                              );
                            })()}
                          </>
                        )}


                        {averageAttendanceTimeDisplay && (
                          <Box
                            sx={{
                              mt: 1.5,
                              p: 2,
                              borderRadius: 2,
                              backgroundColor: theme.palette.action.hover,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <AccessTimeIcon
                              sx={{
                                color: theme.palette.primary.main,
                                mr: 2,
                                fontSize: 20,
                                flexShrink: 0,
                              }}
                            />
                            {
                              selectedCard.ticket?.status === 'closed' && (
                                <Box sx={{ pl: 1 }}>
                                  <Typography
                                    variant='body2'
                                    color='textSecondary'
                                    sx={{
                                      fontSize: '0.75rem',
                                      textTransform: 'uppercase',
                                      letterSpacing: 0.5,
                                    }}
                                  >
                                    Tempo médio de atendimento
                                  </Typography>
                                  <Typography variant='body1' sx={{ fontWeight: 600 }}>
                                    {averageAttendanceTimeDisplay}
                                  </Typography>
                                </Box>
                              )}
                          </Box>
                        )}
                        {selectedDealTicket && selectedCard.ticket.status !== 'closed' && (
                          <>
                            <Box
                              sx={{
                                mt: 1.5,
                                mb: 2,
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: theme.palette.action.hover,
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <HourglassEmptyIcon
                                sx={{
                                  color: theme.palette.primary.main,
                                  mr: 2,
                                  fontSize: 20,
                                  flexShrink: 0,
                                }}
                              />
                              <Box sx={{ pl: 1 }}>
                                <Typography
                                  variant='body2'
                                  color='textSecondary'
                                  sx={{
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                  }}
                                >
                                  Tempo de ociosidade
                                </Typography>
                                <Typography variant='body1' sx={{ fontWeight: 600 }}>
                                  {(() => {
                                    const displayIdleStatus =
                                      idleStatusUpdates[selectedDealTicket?.id] ??
                                      selectedTicketIdleStatus ??
                                      selectedDealTicket?.idleStatus;
                                    const minutesIdle =
                                      displayIdleStatus?.minutesIdle;
                                    return typeof minutesIdle === 'number'
                                      ? `${minutesIdle} min`
                                      : i18n.t('pipelines.details.notAvailable');
                                  })()}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ mt: 1 }}>
                              <TicketResponseTimeIndicator
                                ticketId={selectedDealTicket?.id}
                                ticketStatus={selectedDealTicket?.status}
                              />
                            </Box>
                          </>
                        )}
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Atividades do Estágio */}
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        height: '100%',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 3,
                        background: theme.palette.background.default,
                      }}
                    >
                      <Typography
                        variant='h6'
                        sx={{
                          mb: 3,
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                        }}
                      >
                        {i18n.t('pipelines.details.stageActivities')}
                      </Typography>

                      {stageRequirements.length > 0 ? (
                        <Box>
                          {stageRequirements.map((requirement, index) => (
                            <Paper
                              key={requirement.id}
                              elevation={0}
                              sx={{
                                p: 2,
                                mb: 2,
                                border: `1px solid ${requirement.filled ? theme.palette.success.main : theme.palette.divider}`,
                                borderRadius: 2,
                                backgroundColor: requirement.filled
                                  ? theme.palette.success.light
                                  : theme.palette.background.paper,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  mb: 2,
                                  gap: 2,
                                }}
                              >
                                {requirement.filled ? (
                                  <CheckCircleIcon
                                    sx={{
                                      color: theme.palette.success.main,
                                      fontSize: 24,
                                    }}
                                  />
                                ) : (
                                  <HourglassEmptyIcon
                                    sx={{
                                      color: theme.palette.text.disabled,
                                      fontSize: 24,
                                    }}
                                  />
                                )}
                                <Typography
                                  variant='body1'
                                  sx={{ fontWeight: 500, flex: 1 }}
                                >
                                  {requirement.name}
                                </Typography>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            textAlign: 'center',
                            py: 4,
                            color: theme.palette.text.secondary,
                          }}
                        >
                          <AssignmentIcon
                            sx={{ fontSize: 48, mb: 2, opacity: 0.5 }}
                          />
                          <Typography variant='body1'>
                            {i18n.t('pipelines.details.noActivitiesConfigured')}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </Grid>

                {/* Histórico de Atividades - Pipeline atual sempre; outras pipelines só ao escolher no select */}
                <Box sx={{ mt: 5, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Título e botão na mesma linha */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 2,
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                      }}
                    >
                      Histórico de atendimentos
                      {loadingContactDealsAllPipelines && (
                        <Box component="span" sx={{ ml: 1, fontWeight: 400, fontSize: '0.75rem' }}>
                          (carregando outras pipelines...)
                        </Box>
                      )}
                    </Typography>
                    {contactDealsFromAllPipelines.length === 0 && !loadingContactDealsAllPipelines && selectedCard?.contact?.id && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => fetchContactDealsFromAllPipelines(selectedCard.contact.id)}
                      >
                        Ver atendimentos de outras pipelines
                      </Button>
                    )}
                  </Box>

                  {contactDealsFromAllPipelines.length > 1 ? (
                    <>
                      <FormControl
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ minWidth: 280 }}
                      >
                        <InputLabel id="crm-attendance-select-label">
                          Selecione o atendimento
                        </InputLabel>
                        <Select
                          labelId="crm-attendance-select-label"
                          value={selectedDealIdForHistory ?? selectedCard.id ?? ''}
                          onChange={e =>
                            setSelectedDealIdForHistory(Number(e.target.value))
                          }
                          label="Selecione o atendimento"
                        >
                          {contactDealsList.map((deal, index) => {
                            const stageLabel = deal.stageName ?? getDealStatusLabelForCrm(deal);
                            const dateStr = formatDealDateForCrm(deal);
                            const pipelinePart = deal.pipelineName ? ` · ${deal.pipelineName}` : '';
                            const isCurrent = deal.id === selectedCard?.id;
                            const currentTag = isCurrent ? ' (atual)' : '';
                            const label = `Atendimento ${index + 1} - ${dateStr ? `${stageLabel} (${dateStr})` : stageLabel}${pipelinePart}${currentTag}`;
                            return (
                              <MenuItem key={deal.id} value={deal.id}>
                                {label}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                      <DealActivityHistory
                        dealId={selectedDealIdForHistory ?? selectedCard.id}
                        stages={stagesForDealActivityHistory}
                      />
                    </>
                  ) : (
                    <DealActivityHistory
                      dealId={selectedCard.id}
                      stages={stagesForDealActivityHistory}
                    />
                  )}
                </Box>
              </Box>
            </DialogContent>

            {/* Footer com Ações */}
            <Box
              sx={{
                p: 3,
                borderTop: `1px solid ${theme.palette.divider}`,
                background: theme.palette.background.default,
                borderRadius: '0 0 16px 16px',
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant='outlined'
                  startIcon={<AssignmentIcon />}
                  onClick={handleOpenDealActivitiesModal}
                  disabled={!selectedDealTicket}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Atividades do Estágio
                </Button>
                <Button
                  variant='outlined'
                  onClick={handleCloseModal}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {i18n.t('pipelines.details.close')}
                </Button>
                {selectedCard.ticket?.status !== 'closed' && (
                  <Button
                    variant='contained'
                    startIcon={<ChatIcon />}
                    onClick={() => handleGoToChat(selectedCard.id)}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      },
                    }}
                  >
                    {i18n.t('pipelines.details.goToChat')}
                  </Button>
                )}
              </Box>
            </Box>
          </>
        )}
      </Dialog>

      {/* Modal de Criação de Ticket */}
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={handleCloseNewTicketModal}
      />
      <StageActivitiesModal
        open={dealActivitiesModalOpen}
        onClose={handleCloseDealActivitiesModal}
        ticket={selectedDealTicket}
        onActivitiesSaved={handleDealActivitiesSaved}
      />

      {/* Modal de Pré-requisitos Pendentes */}
      <PreRequirementsModal
        open={preRequirementsModalOpen}
        onClose={() => {
          setPreRequirementsModalOpen(false);
          setDealIdForPreRequirements(null);
          setTargetStageIdForPreRequirements(null);
        }}
        dealId={dealIdForPreRequirements}
        targetStageId={targetStageIdForPreRequirements}
        onRequirementsFilled={async (filledDealId, filledStageId) => {
          // Fazer a requisição de movimento APENAS após fechar o modal e preencher os pré-requisitos
          try {
            const success = await updateDealStage(filledDealId, filledStageId);
            if (success) {
              // Recarregar dados da pipeline
              await fetchPipelineInfo();
              toast.success('Deal movido para "Perdidos" com sucesso!');
            } else {
              toast.error(
                'Erro ao mover deal. Verifique se todos os pré-requisitos foram preenchidos.'
              );
            }
          } catch (error) {
            console.error(
              'Erro ao mover deal após preencher pré-requisitos:',
              error
            );
            toast.error('Erro ao mover deal. Tente novamente.');
          }
        }}
      />
    </Box>
  );
};

export default CrmList;
